import { cn } from '@sistemas/ui'
import { AlertTriangle, Inbox, Loader2, ShieldOff } from 'lucide-react'
import type * as React from 'react'

import { Button } from '@/components/ui/button'

/**
 * Estados padrão de tela. Todo componente interativo do Hub precisa cobrir
 * carregando, vazio, erro e sem-permissão — requisito de aceite
 * (docs/SDD/15-requisitos-nao-funcionais.md §6).
 *
 * Centralizar aqui evita cada tela inventar o seu.
 */

function Frame({
  icon,
  title,
  description,
  action,
  tone = 'muted',
  className,
}: {
  icon: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
  tone?: 'muted' | 'destructive'
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border px-6 py-10 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-md',
          tone === 'destructive'
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted text-muted-foreground',
        )}
        aria-hidden
      >
        {icon}
      </div>
      <div className="space-y-1">
        <p className="text-[13px] font-semibold text-foreground">{title}</p>
        {description && (
          <p className="max-w-sm text-[12px] text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </div>
  )
}

export function Loading({ label = 'Carregando...' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[220px] items-center justify-center gap-2 text-[12px] text-muted-foreground"
    >
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      <span>{label}</span>
    </div>
  )
}

export function Empty({
  title = 'Nada por aqui ainda',
  description,
  action,
  className,
}: {
  title?: string
  description?: string
  action?: React.ReactNode
  /** Para a tela vazia acompanhar a altura de uma página que preenche a faixa. */
  className?: string
}) {
  return (
    <Frame
      icon={<Inbox className="h-5 w-5" />}
      title={title}
      {...(description !== undefined ? { description } : {})}
      {...(action !== undefined ? { action } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  )
}

export function ErrorState({
  title = 'Algo deu errado',
  description = 'Não foi possível carregar esta tela.',
  onRetry,
}: {
  title?: string
  description?: string
  onRetry?: () => void
}) {
  return (
    <Frame
      tone="destructive"
      icon={<AlertTriangle className="h-5 w-5" />}
      title={title}
      description={description}
      {...(onRetry
        ? {
            action: (
              <Button variant="outline" size="sm" onClick={onRetry}>
                Tentar novamente
              </Button>
            ),
          }
        : {})}
    />
  )
}

export function Forbidden({
  description = 'Você não tem permissão para acessar este conteúdo. Fale com o gestor do departamento.',
}: {
  description?: string
}) {
  return (
    <Frame
      icon={<ShieldOff className="h-5 w-5" />}
      title="Acesso restrito"
      description={description}
    />
  )
}
